import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { parseLifecycleResponse } from '@/state/mutations/media-agent-admin/use-media-agent-lifecycle-mutations';
import { LIST_MEDIA_AGENT_POOL_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-pool';
import { LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-bookings';
import { ATTENTION_QUEUE_QUERY_KEY } from '@/state/queries/ops-admin/use-attention-queue';
import { PLATFORM_SUMMARY_QUERY_KEY } from '@/state/queries/ops-admin/use-platform-summary';

export type MediaAgentRecoverMode = 'auto' | 'soft' | 'hard';

export type MediaAgentReissueTokenResult = {
  playerId: string;
  vmId?: string;
  jwtReinjected?: boolean;
};

export type MediaAgentRecoverResult = {
  playerId?: string;
  mode?: MediaAgentRecoverMode;
  result?: string;
  detail?: string;
};

/** Heal actions change pool state and can clear an attention row, so both surfaces refetch. */
function invalidateHealQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [ATTENTION_QUEUE_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [PLATFORM_SUMMARY_QUERY_KEY] });
}

export function useReissueMediaAgentTokenMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(playerId)}/reissue-token`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      return (await parseLifecycleResponse(res, 'reissue-token')) as MediaAgentReissueTokenResult;
    },
    onSuccess: () => invalidateHealQueries(queryClient),
  });
}

export function useRecoverMediaAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: string | { playerId: string; mode?: MediaAgentRecoverMode }) => {
      const playerId = typeof input === 'string' ? input : input.playerId;
      const mode: MediaAgentRecoverMode = (typeof input === 'string' ? undefined : input.mode) ?? 'auto';
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(playerId)}/recover`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      return (await parseLifecycleResponse(res, 'recover')) as MediaAgentRecoverResult;
    },
    onSuccess: () => invalidateHealQueries(queryClient),
  });
}
